import * as React from 'react';
import { FieldArrayWithId, TextFieldElement, UseFieldArrayRemove, UseFieldArrayAppend } from 'react-hook-form-mui';
import Grid from '@mui/material/Grid';
import InspectionReportViewmodel from './Viewmodels/InspectionReportViewmodel';
import TableContainer from '@mui/material/TableContainer';
import ResponsiveTable from '../Utilities/ResponsiveTable';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import Button from '@mui/material/Button';
import { TokenRefreshContext, ResponseType } from '../Contexts/TokenRefreshContext';
import authService from '../api-authorization/AuthorizeService';
import SingleFileUpload from '../Utilities/SingleFileUpload';
import InputLabel from '@mui/material/InputLabel';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import FileViewmodel from '../Utilities/ViewModels/FileViewmodel';
import { Permission } from '../Permissions/Permissions';
import PermissionCheck from '../Utilities/PermissionCheck';
import Typography from '@mui/material/Typography';

interface IProps {
    editing: boolean;
    fields: FieldArrayWithId<InspectionReportViewmodel, 'files', 'id'>[];
    remove: UseFieldArrayRemove;
    append: UseFieldArrayAppend<InspectionReportViewmodel, 'files'>;
}

export default function AttachedFiles(props: IProps) {
    const { editing, fields, remove, append } = props;
    const { crabFetch } = React.useContext(TokenRefreshContext);
    const [open, setOpen] = React.useState(false);
    const [newFile, setNewFile] = React.useState<File | null>(null);
    const [caption, setCaption] = React.useState('');
    const [role, setRole] = React.useState<string>('');

    React.useEffect(() => {
        checkRole();
    }, []);

    const checkRole = async () => {
        const user = await authService.getUser();

        if (user && user.sub) {
            setRole(user.role ? user.role : user.userType ? user.userType : '');
        }
    }

    const download = async (field: FieldArrayWithId<InspectionReportViewmodel, "files", 'id'>) => {
        if (field.fileId) {
            const token = await authService.getAccessToken();

            crabFetch(`InspectionReport/DownloadFile?id=${field.fileId}`, {
                headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
            }, ResponseType.Blob,
                ((blob: Blob) => {
                    const url = URL.createObjectURL(blob);
                    var a = document.createElement('a');
                    a.download = field.fileName;
                    a.href = url;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                })
            );
        } else if (field.file) {
            const url = URL.createObjectURL(field.file);
            var a = document.createElement('a');
            a.download = field.file.name;
            a.href = url;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        }
    }

    const upload = () => {
        const vm = new FileViewmodel();
        vm.file = newFile;
        vm.description = caption;
        vm.fileName = newFile?.name ?? '';
        append(vm);
        closeUpload();
    }

    const closeUpload = () => {
        setNewFile(null);
        setCaption('');
        setOpen(false);
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TableContainer>
                    <ResponsiveTable
                        aria-labelledby="tableTitle"
                        size={'medium'}
                        aria-label="files table"
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>File Name</TableCell>
                                <TableCell />
                                <TableCell>Description</TableCell>
                                {role !== "Client" &&
                                    <TableCell></TableCell>
                                }
                                <PermissionCheck permission={Permission.CreateReport}>
                                    <TableCell />
                                </PermissionCheck>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fields.map((field, index) => {
                                return (
                                    <TableRow key={field.id}>
                                        <TableCell sx={{ backgroundColor: '#fff !important' }}>
                                            <TextField fullWidth size="small" disabled value={field.fileId ? field.fileName : field.file ? field.file.name : ''} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ backgroundColor: '#fff !important', width: 64 }}>
                                            <IconButton size="small" onClick={() => download(field)}><DownloadIcon /></IconButton>
                                        </TableCell>
                                        <TableCell sx={{ backgroundColor: '#fff !important' }}>
                                            <TextFieldElement name={`files.${index}.description`} fullWidth size="small" disabled={!editing} />
                                        </TableCell>
                                        {role !== "Client" &&
                                            <TableCell sx={{ backgroundColor: '#fff !important' }}>
                                                <Typography color="error">{!field.fileInBlob && 'File not found. Please upload again.'}</Typography>
                                            </TableCell>
                                        }
                                        <PermissionCheck permission={Permission.CreateReport}>
                                            <TableCell align="right" sx={{ backgroundColor: '#fff !important', width: 64 }}>
                                                <IconButton size="small" onClick={() => remove(index)} disabled={!editing}><DeleteIcon /></IconButton>
                                            </TableCell>
                                        </PermissionCheck>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </ResponsiveTable>
                </TableContainer>
            </Grid>
            {editing &&
                <PermissionCheck permission={Permission.CreateReport}>
                    <Grid item>
                        <Button variant="contained" size="small" onClick={() => setOpen(true)}>Upload Another File</Button>
                    </Grid>
                </PermissionCheck>
            }
            <Dialog
                open={open}
                onClose={closeUpload}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{"Add Attachment"}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <InputLabel htmlFor="file" shrink>File</InputLabel>
                            <SingleFileUpload onDelete={() => setNewFile(null)} onDrop={setNewFile} file={newFile} />
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel htmlFor="caption" shrink>Description</InputLabel>
                            <TextField name="caption" value={caption} onChange={(e) => setCaption(e.target.value)} required fullWidth size="small" />
                        </Grid>
                    </Grid>
                    <DialogActions>
                        <Button onClick={closeUpload}>Cancel</Button>
                        <Button variant="contained" onClick={upload}>Add File</Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </Grid>
    );
}